import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Stethoscope, Plus, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { IntakeFormPicker, emailFromClientId } from "@/components/assessment/IntakeFormPicker";
import { PrintFormButton } from "@/components/assessment/print/PrintFormDialog";

function StatusBadge({ status }) {
  const map = {
    draft: "border-amber-200 bg-amber-50 text-amber-800",
    finalised: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };
  return (
    <span
      data-testid={`assessment-status-${status}`}
      className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[status] || "border-zinc-200 bg-zinc-50 text-zinc-800"}`}
    >
      {status}
    </span>
  );
}

export default function Assessments() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [modules, setModules] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [intakeToken, setIntakeToken] = useState(null);
  const [selectedModules, setSelectedModules] = useState(["core"]);
  const [creating, setCreating] = useState(false);
  const [autoPick, setAutoPick] = useState(null);          // {module_keys, pathway_key, source, ...}
  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [autoPickLoading, setAutoPickLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/assessments");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load assessments", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  async function loadModules() {
    try {
      const r = await api.get("/assessments/modules");
      setModules(r.data?.modules || []);
    } catch { /* ignore */ }
  }

  // Auto-open dialog from HealthRecord "New assessment" (?new=1&client_email=…&client_name=…).
  useEffect(() => {
    load();
    loadModules();
    if (searchParams.get("new") === "1") {
      const em = searchParams.get("client_email") || "";
      const nm = searchParams.get("client_name") || "";
      if (em) {
        setClientEmail(em);
        setClientId(`acuity:${em}`);
      }
      if (nm) setClientName(nm);
      setShowNew(true);
      // Clean the query so refreshing doesn't reopen.
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      next.delete("client_email");
      next.delete("client_name");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch pathway auto-pick whenever the dialog opens or the client identifier changes.
  useEffect(() => {
    if (!showNew) return;
    // Derive email from client_id if user typed "acuity:foo@bar" and left the email blank.
    let email = clientEmail;
    if (!email && clientId && clientId.includes(":")) {
      const tail = clientId.split(":", 2)[1];
      if (tail && tail.includes("@")) email = tail;
    }
    if (!email) {
      setAutoPick(null);
      return;
    }
    setAutoPickLoading(true);
    setManuallyEdited(false);
    api.get("/assessments/suggest-modules", { params: { client_email: email } })
      .then((r) => {
        const d = r.data || {};
        setAutoPick(d);
        if (Array.isArray(d.module_keys) && d.module_keys.length) {
          setSelectedModules(d.module_keys);
        }
      })
      .catch(() => setAutoPick({ source: "no_data", module_keys: ["core"], pathway_key: "core" }))
      .finally(() => setAutoPickLoading(false));
  }, [showNew, clientEmail, clientId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.client_name || "").toLowerCase().includes(needle) ||
      (r.practitioner_name || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  function toggleModule(key) {
    if (key === "core") return; // core always on
    setManuallyEdited(true);
    setSelectedModules((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  }

  function resetToAutoPick() {
    if (autoPick && Array.isArray(autoPick.module_keys) && autoPick.module_keys.length) {
      setSelectedModules(autoPick.module_keys);
      setManuallyEdited(false);
    }
  }

  async function createNew() {
    if (!clientId.trim() || !clientName.trim()) {
      toast.error("Client ID and name are required");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/assessments", {
        client_id: clientId.trim(),
        client_name: clientName.trim(),
        client_email: clientEmail.trim() || undefined,
        module_keys: selectedModules,
        intake_submission_token: intakeToken || undefined,
      });
      toast.success("Assessment started");
      nav(`/assessments/${r.data.id}`);
    } catch (e) {
      toast.error("Could not create assessment", { description: e?.response?.data?.detail || e.message });
    } finally { setCreating(false); }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Clinical</div>
          <h1 className="mt-1 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
            <Stethoscope className="h-8 w-8" /> Assessments
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Smart practitioner assessments — Core module always on, plus selectable program modules
            (Onero, and more coming). Use during the consultation on tablet or laptop.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PrintFormButton label="Print blank form" size="default" testid="assessments-print-blank-btn" />
          <Button
            variant="outline"
            onClick={() => nav("/reports/clinical-docs")}
            data-testid="assessments-docs-dashboard-btn"
          >
            Documentation dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => nav("/assessments/recovery")}
            data-testid="assessments-vault-btn"
          >
            Recordings vault
          </Button>
          <Button
            onClick={() => setShowNew(true)}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="assessment-new-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New assessment
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search className="h-4 w-4 text-zinc-400" />
        <Input
          placeholder="Search by client or practitioner"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="assessment-search"
        />
      </div>

      <div className="grid gap-3">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-sm text-zinc-500">No assessments yet.</div>
        )}
        {filtered.map((row) => (
          <Card
            key={row.id}
            className="p-4 border border-zinc-200 hover:border-zinc-300 transition-colors cursor-pointer"
            onClick={() => nav(`/assessments/${row.id}`)}
            data-testid={`assessment-row-${row.id}`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-zinc-950 truncate">{row.client_name}</div>
                  <StatusBadge status={row.status} />
                  {(row.modules || []).filter((m) => !m.hidden).map((m) => (
                    <Badge key={m.key} variant="outline" className="text-2xs">
                      {m.key}
                    </Badge>
                  ))}
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span>Practitioner: {row.practitioner_name}</span>
                  <span>Started: {new Date(row.started_at).toLocaleString()}</span>
                  {row.finalised_at && (
                    <span>Finalised: {new Date(row.finalised_at).toLocaleString()}</span>
                  )}
                  <span>{row.response_count || 0} fields filled</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-zinc-400" />
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent
          className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
          data-testid="assessment-new-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
            <DialogTitle>Start a new assessment</DialogTitle>
            <DialogDescription>
              Pick the modules you expect to need — you can add or hide modules on the fly during
              the consultation. Core is always on.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client ID</Label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="e.g. acuity:12345"
                  className="mt-1.5"
                  data-testid="assessment-new-client-id"
                />
              </div>
              <div>
                <Label>Client name</Label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="mt-1.5"
                  data-testid="assessment-new-client-name"
                />
              </div>
            </div>
            <div>
              <Label>Client email (optional — used to auto-pick modules)</Label>
              <Input
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="client@example.com"
                className="mt-1.5"
                data-testid="assessment-new-client-email"
              />
            </div>
            <IntakeFormPicker
              email={clientEmail.trim() || emailFromClientId(clientId.trim())}
              name={clientName.trim()}
              value={intakeToken}
              onChange={setIntakeToken}
            />
            {autoPickLoading && (
              <div className="p-3 rounded border border-zinc-200 bg-zinc-50 text-sm text-zinc-500" data-testid="assessment-auto-pick-loading">
                Looking up recommended modules…
              </div>
            )}
            {!autoPickLoading && autoPick && ["map_id", "map_name", "seed_guess_from_last_appt", "seed_guess_from_explicit", "onboarding_form"].includes(autoPick.source) && (
              <div
                className="p-3 rounded border border-sky-200 bg-sky-50 text-sm text-sky-900 flex items-start justify-between gap-3"
                data-testid="assessment-auto-pick-banner"
              >
                <div>
                  <b>Auto-picked</b> — {autoPick.explanation}
                </div>
                {manuallyEdited && (
                  <button
                    type="button"
                    onClick={resetToAutoPick}
                    className="text-sky-700 hover:text-sky-900 underline text-xs whitespace-nowrap"
                    data-testid="assessment-auto-pick-reset"
                  >
                    Reset to auto
                  </button>
                )}
              </div>
            )}
            {!autoPickLoading && autoPick && autoPick.source === "unmapped_last_appt" && (
              <div
                className="p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900"
                data-testid="assessment-auto-pick-unmapped"
              >
                Client's last appointment (<b>{autoPick.appointment_type_name}</b>) has no
                pathway mapping yet. Pick modules manually — add a mapping in
                <b> Settings → Clinical → Assessment templates</b> to speed this up next time.
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">Modules</Label>
              <div className="grid gap-2">
                {modules.map((m) => {
                  const selected = selectedModules.includes(m.key);
                  const disabled = m.always_on;
                  return (
                    <button
                      type="button"
                      key={m.key}
                      onClick={() => toggleModule(m.key)}
                      disabled={disabled}
                      data-testid={`assessment-module-toggle-${m.key}`}
                      className={`text-left p-3 rounded border transition-colors ${
                        selected
                          ? "border-zinc-950 bg-zinc-50"
                          : "border-zinc-200 hover:border-zinc-300"
                      } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-zinc-950">{m.label}</div>
                        <div className="flex items-center gap-1.5">
                          {m.always_on && <Badge className="text-2xs">always on</Badge>}
                          {selected && !m.always_on && <Badge className="text-2xs">selected</Badge>}
                        </div>
                      </div>
                      <div className="text-xs text-zinc-600 mt-0.5">{m.description}</div>
                      <div className="text-2xs uppercase tracking-wider text-zinc-400 mt-1">
                        {m.section_count} sections · {m.field_count} fields
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white shrink-0">
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button
              onClick={createNew}
              disabled={creating}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="assessment-new-submit"
            >
              {creating ? "Starting…" : "Start assessment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
